import { GraphQL, useRpiGraphqlRequest } from "@rpi/openapi-api";
import {
  filterByMatchingAttributes,
  findAvailableAttributeCombinations,
  findAvailableAttributeValues,
  serializeCombination,
} from "../../hooks/useStandardProducts";
import { ATTRIBUTES, sortDisabledLast } from "./attributeHelpers";
import { ChoiceStep } from "./ChoiceStep";
import { attributesForFiltering, StepProps } from "./ProductCreateNavigator";
import { getDisplayName, getProductImage } from "./attributeValueMappings";
import { ProductImage } from "./ProductImage";
import { TextCard } from "./TextCard";
import { useState } from "react";

export function PaperTypeStep(props: StepProps) {
  const paperKeys = props.attributeKeys;
  const [isCustomerProductCheckLoading, setIsCustomerProductCheckLoading] = useState(false);

  // Pairs reachable from the current upstream selections — the enabled set. Pairs outside it are shown disabled.
  const upstreamProducts = filterByMatchingAttributes(
    props.standardProducts,
    attributesForFiltering(props.state, props.visibleSteps)
  );
  const availablePairIds = new Set(
    findAvailableAttributeCombinations(upstreamProducts, paperKeys).map((combination) =>
      serializeCombination(paperKeys, combination)
    )
  );

  // Resolve the product from the full selection (including the chosen paper) for the duplicate check.
  const selectedProducts = filterByMatchingAttributes(props.standardProducts, props.state.attributes);
  const openapiProductId = selectedProducts.length === 1 ? selectedProducts[0].id : undefined;

  const request = useRpiGraphqlRequest<GraphQL.CustomerProductsQuery, GraphQL.CustomerProductsQueryVariables>(
    GraphQL.CustomerProductsDocument
  );
  const validateOpenapiSkuUnused = async () => {
    if (!openapiProductId) {
      return;
    }

    try {
      setIsCustomerProductCheckLoading(true);
      // Check that the customer hasn't already used this openapi product before advancing to the final step
      // This provides a nicer UX than the error they would get on trying to save the product
      const data = await request({
        limit: 1,
        offset: 0,
        search: {
          inputs: [
            {
              field: GraphQL.CustomerProductSearchField.OpenapiProductId,
              condition: GraphQL.SearchCondition.Equals,
              firstValue: openapiProductId?.toString(),
            },
          ],
        },
      });
      if (data.customerProducts.data.length > 0) {
        return (
          <>
            You already have a product with this exact combination of Cover, Finish, Orientation, Size, Bind, and Paper.
            <br />
            <br />
            Please go back to the previous steps to change one of these selections.
          </>
        );
      }
    } finally {
      setIsCustomerProductCheckLoading(false);
    }
  };

  return (
    <ChoiceStep
      {...props}
      // Show every (weight, style) pair in the catalog; pairs that exist in no product are never enumerated and stay hidden.
      getFilterAttributes={() => ({})}
      sortCombinations={(combinations) =>
        sortDisabledLast(combinations, (c) => !availablePairIds.has(serializeCombination(paperKeys, c)))
      }
      renderCard={(combination, id) => {
        const weightName = getDisplayName(ATTRIBUTES.TEXT_STOCK, String(combination[ATTRIBUTES.TEXT_STOCK]));
        const styleName = getDisplayName(ATTRIBUTES.STOCK_STYLE, String(combination[ATTRIBUTES.STOCK_STYLE]));
        if (!weightName || !styleName) return null;

        const disabled = !availablePairIds.has(id);
        const renderTooltip = disabled
          ? () => <>This paper type is not available in the bleed style you’ve selected.</>
          : undefined;

        return (
          <TextCard
            key={id}
            value={id}
            displayName={`${weightName} ${styleName}`}
            disabled={disabled}
            renderTooltip={renderTooltip}
          />
        );
      }}
      renderImage={(combination) => {
        const weightName = getDisplayName(ATTRIBUTES.TEXT_STOCK, String(combination[ATTRIBUTES.TEXT_STOCK]));
        const styleName = getDisplayName(ATTRIBUTES.STOCK_STYLE, String(combination[ATTRIBUTES.STOCK_STYLE]));
        const image = getProductImage(ATTRIBUTES.STOCK_STYLE, String(combination[ATTRIBUTES.STOCK_STYLE]));
        return image && weightName && styleName ? (
          <ProductImage displayName={`${weightName} ${styleName}`} image={image} />
        ) : null;
      }}
      flexDirection="column"
      onAdditionalValidation={validateOpenapiSkuUnused}
      isLoading={isCustomerProductCheckLoading}
    />
  );
}

// The wizard card annotates the shared interior-color name with its print ratio, per Figma Step 7.1
// (e.g. "Full Color (4/4)"); the shared mapping stays plain ("Full Color") for the product details view.
const INTERIOR_COLOR_RATIOS = new Map<string, string>([
  ["44", "4/4"],
  ["11", "1/1"],
]);

export function InteriorColorStep(props: StepProps) {
  // Values reachable from the current upstream selections (paper, size, bleed style, …) — the enabled set.
  const upstreamProducts = filterByMatchingAttributes(
    props.standardProducts,
    attributesForFiltering(props.state, props.visibleSteps)
  );
  const availableValues = new Set(findAvailableAttributeValues(upstreamProducts, ATTRIBUTES.TEXT_PRINT_MODE));

  return (
    <ChoiceStep
      {...props}
      // Show every interior-color value in the catalog; values unavailable for the current selections render disabled.
      getFilterAttributes={() => ({})}
      sortCombinations={(combinations) =>
        sortDisabledLast(combinations, (c) => !availableValues.has(String(c[ATTRIBUTES.TEXT_PRINT_MODE])))
      }
      renderCard={(combination, id) => {
        const value = String(combination[ATTRIBUTES.TEXT_PRINT_MODE]);
        const baseName = getDisplayName(ATTRIBUTES.TEXT_PRINT_MODE, value);
        if (!baseName) return null;
        const ratio = INTERIOR_COLOR_RATIOS.get(value);
        const displayName = ratio ? `${baseName} (${ratio})` : baseName;

        const disabled = !availableValues.has(value);
        const renderTooltip = disabled
          ? () => (
              <>
                This interior color is not available in the size and bleed style you’ve selected. If your design is
                black and white, it will remain black and white but will be printed using 4-color CMYK (4/4).
              </>
            )
          : undefined;

        return (
          <TextCard key={id} value={id} displayName={displayName} disabled={disabled} renderTooltip={renderTooltip} />
        );
      }}
      flexDirection="column"
    />
  );
}
